// Pure styling objects for POSDashBoard - no logic, no side effects.
// Palette matches InvoiceConfiguration.js (primary #5156be / #4338ca,
// gray neutrals) so both pages feel consistent. Status colors (success/
// danger/warning) are intentionally muted/desaturated rather than pure
// "traffic light" hues, so the list stays calm and readable at a glance
// even when many rows need attention at once.
//
// RESPONSIVENESS NOTE: most spacing/layout below is expressed as inline
// style objects (as before), but a handful of layout properties that
// genuinely need to change per-breakpoint (flex-direction, grid columns,
// padding on very small screens, chevron position) are handled by the
// `posDashboardResponsiveCSS` stylesheet exported at the bottom and
// injected once via a <style> tag in the component. Those rules use
// !important deliberately, since they exist specifically to override the
// inline styles at narrow widths — that's the whole point of them.

/* ============================================================
   DESIGN TOKENS
   ============================================================ */
export const colors = {
    // Brand (indigo/violet) — matches InvoiceConfiguration's #5156be / #4338ca
    primary: "#5156be",
    primaryDark: "#4338ca",
    primaryLight: "#eef2ff",
    primaryBorder: "#c7d2fe",
    primaryFocusRing: "rgba(81,86,190,0.2)",

    // Success — muted sage green, not a saturated "go" green
    success: "#3f9668",
    successDark: "#2c7350",
    successLight: "#edf7f1",
    successBorder: "#cde7d9",

    // Danger — muted brick/terracotta red instead of alarm red
    danger: "#c1554f",
    dangerDark: "#9c423d",
    dangerLight: "#fbf1f0",
    dangerBorder: "#eed6d3",

    // Warning — soft amber/ochre, for server-machine highlight + pending-type flags
    warning: "#c2872e",
    warningDark: "#96691f",
    warningLight: "#fbf5ea",
    warningBorder: "#ecdcb9",

    // Secondary accent (POS chip, kept distinct from brand indigo)
    accent: "#3592a3",
    accentDark: "#296e7b",
    accentLight: "#eef8fa",
    accentBorder: "#c9e6eb",

    // Neutrals — Tailwind "gray" scale, matching InvoiceConfiguration's
    // #111827 / #374151 / #9ca3af / #e5e7eb / #f3f4f6 / #f9fafb usage
    gray900: "#111827",
    gray700: "#374151",
    gray600: "#4b5563",
    gray500: "#6b7280",
    gray400: "#9ca3af",
    gray300: "#d1d5db",
    gray200: "#e5e7eb",
    gray100: "#f3f4f6",
    gray50: "#f9fafb",
    white: "#ffffff",
};

/* ============================================================
   FILTER CARD
   ============================================================ */
export const filterCardStyle = {
    width: "100%",
    background: colors.white,
    padding: "20px 22px",
    borderRadius: "16px",
    border: `1px solid ${colors.gray200}`,
    boxShadow: "0 6px 18px rgba(17,24,39,0.045)",
    marginBottom: "18px",
};

export const filterHeaderRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "18px",
};

export const filterHeaderTitleStyle = {
    fontSize: "15px",
    fontWeight: 600,
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
    gap: "8px",
};

export const filterFieldLabelStyle = {
    display: "block",
    fontSize: "11.5px",
    fontWeight: 600,
    color: colors.gray500,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginBottom: "7px",
};

// legacy alias, kept in case referenced elsewhere
export const partyLabelStyle = filterFieldLabelStyle;

export const partySelectStyles = {
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};

export const statusSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: "38px",
        height: "auto",
        borderRadius: "10px",
        borderColor: state.isFocused ? colors.primary : colors.gray200,
        boxShadow: state.isFocused ? `0 0 0 2px ${colors.primaryFocusRing}` : "none",
        "&:hover": { borderColor: colors.primary },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 999,
        borderRadius: "10px",
        overflow: "hidden",
        boxShadow: "0 12px 28px rgba(17,24,39,0.08)",
        border: `1px solid ${colors.gray100}`,
    }),
    menuList: (base) => ({
        ...base,
        zIndex: 999,
        padding: "4px",
    }),
    valueContainer: (base) => ({
        ...base,
        flexWrap: "wrap",
        alignItems: "flex-start",
    }),
    multiValue: (base) => ({
        ...base,
        margin: "3px 4px 3px 0",
        borderRadius: "6px",
        background: colors.primaryLight,
        border: `1px solid ${colors.primaryBorder}`,
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: colors.primaryDark,
        fontWeight: 600,
        fontSize: "12px",
    }),
    option: (base, state) => ({
        ...base,
        borderRadius: "7px",
        backgroundColor: state.isDisabled
            ? colors.gray100
            : state.isSelected
                ? colors.primary
                : state.isFocused
                    ? colors.primaryLight
                    : colors.white,
        color: state.isDisabled
            ? colors.gray400
            : state.isSelected
                ? colors.white
                : colors.gray700,
        cursor: state.isDisabled ? "not-allowed" : "pointer",
        fontSize: "13px",
    }),
};

export const serverMachineCheckboxStyle = {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: colors.primary,
};

export const serverMachineLabelStyle = {
    fontWeight: 500,
    fontSize: "13px",
    color: colors.gray700,
    whiteSpace: "nowrap",
    marginLeft: "8px",
    marginBottom: 0,
    cursor: "pointer",
};

export const goButtonStyle = {
    background: colors.primary,
    border: "none",
    padding: "9px 30px",
    fontWeight: 600,
    fontSize: "14px",
    borderRadius: "10px",
    color: colors.white,
    boxShadow: "0 4px 12px rgba(81,86,190,0.2)",
};

/* ============================================================
   LEGEND — tiny key so the color language is explained once,
   instead of the user having to infer it row by row.
   ============================================================ */
export const legendRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "18px",
    marginTop: "14px",
    paddingTop: "14px",
    borderTop: `1px dashed ${colors.gray200}`,
};

export const legendItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12px",
    color: colors.gray600,
    fontWeight: 500,
};

/* ============================================================
   SUMMARY / TAB CARDS
   These double as the page's view switcher — click a card to
   filter the list to that group. Ordered left-to-right as the
   user reads them: Needs Attention -> Running OK -> Offline ->
   All Machines. The active card is highlighted with a colored
   border + background tint + top accent bar, and a small check
   badge, so it's unmistakable which view is currently selected
   even for someone unfamiliar with the page.
   ============================================================ */
export const summaryStripStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "12px",
    marginBottom: "18px",
};

const summaryToneMap = {
    neutral: { bg: colors.primaryLight, fg: colors.primary, border: colors.primaryBorder, valueColor: colors.gray900 },
    success: { bg: colors.successLight, fg: colors.success, border: colors.successBorder, valueColor: colors.successDark },
    danger: { bg: colors.dangerLight, fg: colors.danger, border: colors.dangerBorder, valueColor: colors.dangerDark },
    warning: { bg: colors.warningLight, fg: colors.warning, border: colors.warningBorder, valueColor: colors.warningDark },
};

// Cards are neutral white by default with a soft icon "chip" carrying the
// tone color; the active card picks up a tinted background, a matching
// border, a top accent bar, and a lifted shadow so it reads clearly as
// "this is the view you're looking at" rather than just another stat.
// Borders are intentionally 1px (not 1.5px) even when active — the tint +
// accent bar + shadow already carry the "selected" signal, so a heavier
// border just adds visual weight without adding clarity.
export const getSummaryCardStyle = (tone = "neutral", isActive = false) => {
    const t = summaryToneMap[tone] || summaryToneMap.neutral;
    return {
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: "13px",
        background: isActive ? t.bg : colors.white,
        border: `1px solid ${isActive ? t.border : colors.gray200}`,
        borderRadius: "14px",
        padding: "15px 16px",
        boxShadow: isActive ? "0 6px 16px rgba(17,24,39,0.07)" : "0 1px 3px rgba(17,24,39,0.04)",
        cursor: "pointer",
        userSelect: "none",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease",
        overflow: "hidden",
    };
};

// Thin accent bar along the top edge of the active card — a familiar
// "selected tab" cue that still works if someone can't distinguish the
// background tint.
export const getSummaryCardAccentStyle = (tone = "neutral") => {
    const t = summaryToneMap[tone] || summaryToneMap.neutral;
    return {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        background: t.fg,
    };
};

export const summaryCardHoverStyle = {
    boxShadow: "0 6px 16px rgba(17,24,39,0.07)",
};

export const getSummaryIconChipStyle = (tone) => {
    const t = summaryToneMap[tone] || summaryToneMap.neutral;
    return {
        width: "38px",
        height: "38px",
        flexShrink: 0,
        borderRadius: "11px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: t.bg,
        color: t.fg,
        fontSize: "18px",
    };
};

export const summaryCardTextWrapStyle = {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
};

export const summaryCardLabelStyle = {
    fontSize: "12px",
    fontWeight: 700,
    color: colors.gray700,
    marginBottom: "2px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
};

export const summaryCardDescStyle = {
    fontSize: "10.5px",
    color: colors.gray500,
    fontWeight: 500,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
};

export const getSummaryCardValueStyle = (tone) => {
    const t = summaryToneMap[tone] || summaryToneMap.neutral;
    return {
        fontSize: "22px",
        fontWeight: 700,
        color: t.valueColor,
        lineHeight: 1.15,
    };
};

/* ============================================================
   TABS — "Needs Attention" (default) vs "All Machines".
   Sit on top of the list, underline-style. (Legacy — currently
   unused now that summary cards double as the switcher, kept in
   case referenced elsewhere.)
   ============================================================ */
export const tabRowStyle = {
    display: "flex",
    gap: "6px",
    padding: "14px 20px 0 20px",
    borderBottom: `1px solid ${colors.gray200}`,
};

export const getTabButtonStyle = (isActive) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    borderRadius: "10px 10px 0 0",
    border: "none",
    borderBottom: isActive ? `2.5px solid ${colors.primary}` : "2.5px solid transparent",
    background: isActive ? colors.primaryLight : "transparent",
    color: isActive ? colors.primaryDark : colors.gray500,
    fontWeight: 700,
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.15s ease",
    marginBottom: "-1px",
});

export const getTabCountStyle = (isActive) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "20px",
    height: "20px",
    padding: "0 6px",
    borderRadius: "999px",
    background: isActive ? colors.white : colors.gray100,
    color: isActive ? colors.primaryDark : colors.gray500,
    fontSize: "11px",
    fontWeight: 700,
});

/* ============================================================
   ATTENTION SUB-TABS
   ============================================================ */
export const subTabRowStyle = {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
    margin: "0 0 14px",
};

export const getSubTabButtonStyle = (isActive) => ({
    padding: isActive ? "6px 10px 6px 14px" : "6px 14px",
    borderRadius: "999px",
    border: `1px solid ${isActive ? colors.primary : colors.gray200}`,
    background: isActive ? colors.primary : colors.white,
    color: isActive ? colors.white : colors.gray700,
    fontSize: "12.5px",
    fontWeight: 600,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    boxShadow: isActive ? "0 3px 8px rgba(81,86,190,0.18)" : "none",
    transition: "background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
});

export const subTabClearIconStyle = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "16px",
    height: "16px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.22)",
    fontSize: "13px",
    lineHeight: 1,
};

/* ============================================================
   OUTER CARD (wraps the tabs + the row list below)
   ============================================================ */
export const tableCardStyle = {
    width: "100%",
    background: colors.white,
    borderRadius: "16px",
    border: `1px solid ${colors.gray200}`,
    boxShadow: "0 6px 18px rgba(17,24,39,0.045)",
    overflow: "hidden",
};

export const emptyStateStyle = {
    textAlign: "center",
    color: colors.gray400,
    fontSize: "14px",
    fontWeight: 500,
    padding: "56px 16px",
};

// Positive variant of the empty state — shown on the "Needs Attention"
// tab when there is genuinely nothing to flag.
export const emptyStateSuccessStyle = {
    ...emptyStateStyle,
    color: colors.successDark,
};

/* ============================================================
   MACHINE LIST (row cards) — replaces the old dense <table>.
   Each row is a compact, scannable summary (name + only the
   badges that matter); the full breakdown lives in the detail
   modal opened on click.

   Layout is a flex row (name+id | chips | chevron) on desktop.
   Below 640px, `.posdb-row-card` in the responsive stylesheet
   forces this to stack vertically and moves the chevron to a
   small caret in the corner instead of the trailing column.
   ============================================================ */
export const listContainerStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    padding: "14px",
    maxHeight: "70vh",
    overflowY: "auto",
};

// className "posdb-row-card" carries the responsive flex-direction switch;
// this object supplies everything that doesn't change per-breakpoint.
export const getRowCardStyle = () => ({
    position: "relative",
    display: "flex",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "10px 16px",
    padding: "14px 40px 14px 16px",
    borderRadius: "12px",
    border: `1px solid ${colors.gray200}`,
    background: colors.white,
    cursor: "pointer",
    transition: "box-shadow 0.15s ease, border-color 0.15s ease",
});

export const rowCardHoverStyle = {
    boxShadow: "0 6px 16px rgba(17,24,39,0.07)",
    borderColor: colors.primaryBorder,
};

// className "posdb-row-left" carries the responsive flex-basis switch.
export const rowCardLeftStyle = {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "180px",
    flex: "1 1 220px",
};

export const getRowStatusDotStyle = (tone) => {
    const toneMap = {
        success: colors.success,
        danger: colors.danger,
        warning: colors.warning,
        neutral: colors.gray400,
    };
    return {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: toneMap[tone] || toneMap.neutral,
        flexShrink: 0,
    };
};

export const rowCardNameStyle = {
    fontWeight: 600,
    fontSize: "13.5px",
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

export const rowCardMacStyle = {
    fontSize: "11px",
    color: colors.gray400,
    marginTop: "2px",
    fontFamily: "monospace",
};

// className "posdb-row-chips" carries the responsive flex-basis switch.
export const rowCardChipsStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "flex-start",
    flex: "2 1 300px",
    minWidth: "200px",
};

// className "posdb-row-chevron" carries the responsive repositioning.
export const rowChevronStyle = {
    color: colors.gray300,
    fontSize: "20px",
    flexShrink: 0,
    position: "absolute",
    top: "50%",
    right: "12px",
    transform: "translateY(-50%)",
};

/* ============================================================
   DETAIL MODAL — opened on row click. Holds everything the
   compact row doesn't have room for (versions, last-synced
   timeline, invoice/delete mismatch detail).
   ============================================================ */
export const modalHeaderStyle = {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "18px 22px",
    borderBottom: `1px solid ${colors.gray200}`,
    background: colors.gray50,
};

export const modalTitleStyle = {
    fontSize: "16px",
    fontWeight: 700,
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

export const modalCloseButtonStyle = {
    border: "none",
    background: "transparent",
    fontSize: "22px",
    lineHeight: 1,
    color: colors.gray400,
    cursor: "pointer",
    padding: "2px",
};

export const modalBodyWrapStyle = {
    padding: "20px 22px",
    display: "flex",
    flexDirection: "column",
    gap: "18px",
    maxHeight: "65vh",
    overflowY: "auto",
};

export const modalSectionTitleStyle = {
    fontSize: "11px",
    fontWeight: 700,
    color: colors.gray500,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    marginBottom: "9px",
};

export const modalChipsRowStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    alignItems: "flex-start",
};

/* ============================================================
   PARTY / MACHINE (shared name/badge bits used in both the row
   card and the modal header)
   ============================================================ */
export const partyNameStyle = {
    fontWeight: 600,
    fontSize: "13.5px",
    color: colors.gray900,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
};

// Softer "chip" instead of a solid amber block with white text.
export const serverBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    background: colors.warningLight,
    color: colors.warningDark,
    border: `1px solid ${colors.warningBorder}`,
    padding: "2px 8px",
    fontSize: "10px",
    borderRadius: "999px",
    fontWeight: 700,
    letterSpacing: "0.03em",
};

// Small neutral chip showing the machine's ClientID — the actual unique
// identifier for the machine, distinct from the party/customer name shown
// as the row's main label. Kept visually quiet (gray, monospace) so it
// reads as reference info rather than competing with the name or the
// SERVER badge.
export const clientIdBadgeStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    background: colors.gray100,
    color: colors.gray600,
    border: `1px solid ${colors.gray200}`,
    padding: "2px 7px",
    fontSize: "10.5px",
    borderRadius: "999px",
    fontWeight: 600,
    fontFamily: "monospace",
    letterSpacing: "0.01em",
};

export const macAddressStyle = {
    fontSize: "11.5px",
    color: colors.gray400,
    marginTop: "3px",
    fontFamily: "monospace",
};

/* ============================================================
   LAST SYNCED (CreatedOnList) CHIPS
   ============================================================ */
export const createdOnListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
};

// Colored to match the Versions section exactly: POS = accent (teal),
// Service = primary (indigo) — so the same machine's POS/Service data
// reads as the same "color identity" everywhere.
export const getCreatedOnBadgeStyle = (isPos) => ({
    display: "flex",
    alignItems: "center",
    padding: "6px 10px",
    fontSize: "12px",
    borderRadius: "10px",
    background: isPos ? colors.accentLight : colors.primaryLight,
    color: isPos ? colors.accentDark : colors.primaryDark,
    fontWeight: 700,
    border: `1px solid ${isPos ? colors.accentBorder : colors.primaryBorder}`,
    width: "fit-content",
    maxWidth: "100%",
    cursor: "default",
});

export const createdOnLabelStyle = {
    fontWeight: 700,
    marginRight: "6px",
};

export const createdOnIconStyle = {
    marginRight: "6px",
    fontSize: "15px",
};

/* ============================================================
   VERSIONS (POS / Service)
   ============================================================ */
export const getVersionCardStyle = (isPosVersion) => ({
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px 10px",
    padding: "8px 10px",
    borderRadius: "10px",
    background: isPosVersion ? colors.accentLight : colors.primaryLight,
    border: `1px solid ${isPosVersion ? colors.accentBorder : colors.primaryBorder}`,
    marginBottom: "6px",
    fontSize: "12.5px",
});

export const versionLabelChipStyle = (isPosVersion) => ({
    fontWeight: 700,
    fontSize: "11px",
    color: isPosVersion ? colors.accentDark : colors.primaryDark,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
});

export const versionDetailsStyle = {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: colors.gray500,
    flexWrap: "wrap",
};

export const versionValueStyle = {
    color: colors.gray900,
    fontWeight: 600,
    wordBreak: "break-word",
};

// Compact "all good" chip shown instead of the running→current arrow
// whenever a version isn't due for an update.
export const versionOkStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "5px",
    color: colors.successDark,
    fontWeight: 700,
    fontSize: "12px",
};

export const updateRequiredStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "11px",
    color: colors.dangerDark,
    background: colors.dangerLight,
    border: `1px solid ${colors.dangerBorder}`,
    fontWeight: 700,
    padding: "2px 8px",
    borderRadius: "999px",
};

/* ============================================================
   INVOICE / DELETE MISMATCH CARDS (shown inside the modal)
   ============================================================ */
export const invoiceMismatchCardStyle = {
    padding: "10px 12px",
    borderRadius: "10px",
    background: colors.dangerLight,
    border: `1px solid ${colors.dangerBorder}`,
    width: "fit-content",
    maxWidth: "100%",
};

export const deleteMismatchCardStyle = {
    padding: "10px 12px",
    borderRadius: "10px",
    background: colors.warningLight,
    border: `1px solid ${colors.warningBorder}`,
    width: "fit-content",
    maxWidth: "100%",
};

export const invoiceDetailsStyle = {
    fontSize: "12px",
    marginTop: "6px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
};

export const invoiceNumberStyle = {
    fontSize: "11px",
    color: colors.gray500,
    marginLeft: "4px",
};

export const invoiceMismatchMessageStyle = {
    marginTop: "6px",
    fontSize: "11px",
    color: colors.dangerDark,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: "4px",
};

export const deleteMismatchMessageStyle = {
    marginTop: "6px",
    fontSize: "11px",
    color: colors.warningDark,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    gap: "4px",
};

/* ============================================================
   STATUS / CHIP TONE HELPERS — shared by the row-card chips, the
   modal's status row, and the "additional flags" section.
   ============================================================ */
export const versionMismatchListStyle = {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
};

// Decide a badge's tone from its status text.
export const getStatusTone = (status) => {
    if (!status) return "neutral";
    const s = status.toUpperCase();
    if (s === "OK") return "success";
    if (s.includes("OFFLINE")) return "danger";
    return "warning";
};

// Picks a boxicons class based on what the chip actually says, not just
// its tone — so "Offline", "Delete Not Synced", "POS Version Mismatch"
// etc. each get their own recognizable icon instead of a generic dot.
export function getStatusIconClass(text = "", tone = "neutral") {
    const t = (text || "").toUpperCase();

    if (t.includes("OFFLINE")) return "bx-wifi-off";
    if (t === "SERVICE NOT WORKING") return "bx-pulse";   // heartbeat-gap icon — distinct from the cog used for version mismatch
    if (t.includes("DELETE")) return "bx-trash";
    if (t.includes("INVOICE")) return "bx-receipt";
    if (t.includes("SERVICE") && t.includes("MISMATCH")) return "bx-cog";
    if (t.includes("POS") && t.includes("MISMATCH")) return "bx-desktop";
    if (t.includes("VERSION MISMATCH")) return "bx-git-compare";
    if (t === "OK" || t.includes("ALL OK")) return "bx-check-circle";

    const toneIconMap = {
        success: "bx-check-circle",
        danger: "bx-error-circle",
        warning: "bx-error",
        neutral: "bx-info-circle",
    };
    return toneIconMap[tone] || toneIconMap.neutral;
}

// Small soft pill — the one chip style used everywhere (row cards, modal
// status row, modal "additional flags"). Border is intentionally very
// light (it's there for definition on white backgrounds, not as a visual
// anchor) — the tint + text color carry the tone.
export const getStatusBadgeStyle = (tone) => {
    const toneMap = {
        success: { bg: colors.successLight, border: colors.successBorder, text: colors.successDark },
        danger: { bg: colors.dangerLight, border: colors.dangerBorder, text: colors.dangerDark },
        warning: { bg: colors.warningLight, border: colors.warningBorder, text: colors.warningDark },
        neutral: { bg: colors.gray100, border: colors.gray200, text: colors.gray600 },
    };
    const t = toneMap[tone] || toneMap.neutral;

    return {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        width: "fit-content",
        maxWidth: "100%",
        padding: "4px 10px 4px 8px",
        borderRadius: "999px",
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.01em",
    };
};

// Two-line variant of the status pill — used when a chip needs to show a
// short label plus a supporting detail line (exact numbers, or a relative
// time + its absolute timestamp) without cramming everything onto one row.
// className "posdb-status-badge-detailed" lets the responsive stylesheet
// relax maxWidth to 100% on very small screens.
export const getStatusBadgeDetailedStyle = (tone) => {
    const toneMap = {
        success: { bg: colors.successLight, border: colors.successBorder, text: colors.successDark },
        danger: { bg: colors.dangerLight, border: colors.dangerBorder, text: colors.dangerDark },
        warning: { bg: colors.warningLight, border: colors.warningBorder, text: colors.warningDark },
        neutral: { bg: colors.gray100, border: colors.gray200, text: colors.gray600 },
    };
    const t = toneMap[tone] || toneMap.neutral;

    return {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: "6px",
        width: "fit-content",
        maxWidth: "min(280px, 100%)",
        minWidth: "0",
        padding: "10px 12px",
        borderRadius: "10px",           // was 999px (pill) — now a squared card
        background: t.bg,
        border: `1px solid ${t.border}`,
        color: t.text,
        fontSize: "11.5px",
        fontWeight: 700,
        letterSpacing: "0.01em",
        whiteSpace: "normal",
        lineHeight: 1.25,
    };
};

export const statusBadgeDetailTextStyle = {
    fontSize: "10.5px",
    fontWeight: 600,
    opacity: 0.85,
    letterSpacing: "0",
    marginLeft: "0",              // was 18px — no longer indented under the icon
    marginTop: "2px",
    lineHeight: 1.4,
    whiteSpace: "pre-line",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
};

export const statusBadgeTopRowStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
};

export const getStatusDotStyle = (tone) => {
    const toneMap = {
        success: colors.success,
        danger: colors.danger,
        warning: colors.warning,
        neutral: colors.gray400,
    };
    return {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        background: toneMap[tone] || toneMap.neutral,
        flexShrink: 0,
    };
};

// Icon version of the row-card's left-hand status indicator — a soft
// tinted circle carrying the status icon, replacing the plain dot.
export const getRowStatusIconWrapStyle = (tone) => {
    const toneMap = {
        success: { bg: colors.successLight, fg: colors.success },
        danger: { bg: colors.dangerLight, fg: colors.danger },
        warning: { bg: colors.warningLight, fg: colors.warning },
        neutral: { bg: colors.gray100, fg: colors.gray400 },
    };
    const t = toneMap[tone] || toneMap.neutral;
    return {
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: t.bg,
        color: t.fg,
        fontSize: "13px",
        flexShrink: 0,
    };
};

// Icon version of the tiny dot inside each status pill/badge.
export const getStatusIconStyle = (tone) => {
    const toneMap = {
        success: colors.success,
        danger: colors.danger,
        warning: colors.warning,
        neutral: colors.gray400,
    };
    return {
        fontSize: "12px",
        color: toneMap[tone] || toneMap.neutral,
        flexShrink: 0,
        display: "inline-flex",
        alignItems: "center",
    };
};

export const clientIdRowStyle = {
    marginTop: "4px",
};

// kept for backward compatibility with any direct imports
export const versionMismatchItemStyle = getStatusBadgeStyle("warning");

// No longer branches on server/non-server — the SERVER badge and its
// amber left-border tint have been removed from the row card entirely;
// server status is still visible in the modal via the Party/machine info,
// just not called out with special styling on the compact card.

/* ============================================================
   RESPONSIVE STYLESHEET
   Injected once via a <style> tag in the component. Handles the
   handful of layout properties that genuinely need to change per
   breakpoint rather than just scale down. Uses !important where
   it needs to win over the inline style objects above — that's
   intentional, not a hack: these rules exist specifically to
   override the desktop-oriented inline layout at narrow widths.
   ============================================================ */
export const posDashboardResponsiveCSS = `
.posdb-summary-strip {
    display: grid;
}
@media (max-width: 620px) {
    .posdb-summary-strip {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 10px !important;
    }
}
@media (max-width: 400px) {
    .posdb-summary-strip {
        grid-template-columns: 1fr !important;
    }
}

.posdb-subtabs {
    display: flex;
}
@media (max-width: 640px) {
    .posdb-subtabs {
        flex-wrap: nowrap !important;
        overflow-x: auto !important;
        padding-bottom: 6px !important;
        margin-right: -4px !important;
        scrollbar-width: thin;
    }
    .posdb-subtabs::-webkit-scrollbar {
        height: 4px;
    }
    .posdb-subtabs::-webkit-scrollbar-thumb {
        background: ${colors.gray300};
        border-radius: 999px;
    }
    .posdb-subtabs button {
        flex: 0 0 auto !important;
    }
}

@media (max-width: 640px) {
    .posdb-row-card {
        flex-direction: column !important;
        padding: 14px 38px 14px 14px !important;
    }
    .posdb-row-left,
    .posdb-row-chips {
        flex-basis: 100% !important;
        width: 100% !important;
        min-width: 0 !important;
    }
    .posdb-row-chevron {
        top: 16px !important;
        transform: none !important;
    }
    .posdb-status-badge-detailed {
        max-width: 100% !important;
    }
}

@media (max-width: 480px) {
    .posdb-modal-header {
        padding: 14px 16px !important;
    }
    .posdb-modal-body {
        padding: 16px !important;
        gap: 16px !important;
    }
    .posdb-filter-card {
        padding: 16px !important;
    }
    .posdb-summary-card {
        padding: 12px 13px !important;
        gap: 10px !important;
    }
}

@media (max-width: 640px) {
    .posdb-filter-align-label {
        display: none !important;
    }
    .posdb-go-col {
        margin-top: 2px;
    }
    .posdb-go-col button {
        width: 100% !important;
    }
}
`;